/**
 * Utility function to sanitize search parameters to prevent security vulnerabilities
 * such as XSS attacks, SQL injection via API calls, and parameter pollution.
 */

/**
 * Sanitizes a string value by removing potentially dangerous characters
 * @param value The value to sanitize
 * @returns The sanitized value
 */
export function sanitizeString(
  value: string | null | undefined,
): string | undefined {
  if (value === undefined || value === null) {
    return undefined
  }

  try {
    // Decode URL-encoded characters first (handle potential double-encoding)
    let decoded = decodeURIComponent(value)

    // Handle path traversal attempts
    // Remove leading dots, slashes, and URL-encoded versions of them
    decoded = decoded.replace(/^[.\/%]+/, '')

    // Remove URL-encoded path traversal patterns (%2e%2e%2f, etc.)
    decoded = decoded.replace(/%2e/gi, '.')
    decoded = decoded.replace(/%2f/gi, '/')
    decoded = decoded.replace(/\.\.\//g, '')
    decoded = decoded.replace(/\.\.\\+/g, '')
    decoded = decoded.replace(/^\.+[\\\/]*/g, '')

    // Basic sanitization: trim and remove script tags
    let sanitized = decoded.trim()

    // Remove potential script tags and dangerous HTML characters
    sanitized = sanitized.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      '',
    )
    sanitized = sanitized.replace(/[<>'"&]/g, (char) => {
      switch (char) {
        case '<':
          return '&lt;'
        case '>':
          return '&gt;'
        case '"':
          return '&quot;'
        case "'":
          return '&#39;'
        case '&':
          return '&amp;'
        default:
          return char
      }
    })

    // Remove any remaining control characters
    sanitized = sanitized.replace(/[\x00-\x1F\x7F-\x9F]/g, '')

    // Remove any path traversal remnants that might have survived decoding
    sanitized = sanitized.replace(/\.\//g, '')
    sanitized = sanitized.replace(/\.\\/g, '')

    return sanitized
  } catch (error) {
    // If decoding fails, apply sanitization directly to the input
    // This handles cases where the input isn't actually URL-encoded
    let sanitized = value.trim()

    // Remove path traversal attempts
    sanitized = sanitized.replace(/^[.\/%]+/, '')
    sanitized = sanitized.replace(/\.\.\//g, '')
    sanitized = sanitized.replace(/\.\.\\+/g, '')

    // Remove potential script tags and dangerous HTML characters
    sanitized = sanitized.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      '',
    )
    sanitized = sanitized.replace(/[<>'"&]/g, (char) => {
      switch (char) {
        case '<':
          return '&lt;'
        case '>':
          return '&gt;'
        case '"':
          return '&quot;'
        case "'":
          return '&#39;'
        case '&':
          return '&amp;'
        default:
          return char
      }
    })

    return sanitized
  }
}

/**
 * Sanitizes a numeric string, ensuring it only contains numbers
 * @param value The value to sanitize
 * @returns The sanitized numeric string or undefined if invalid
 */
export function sanitizeNumeric(
  value: string | null | undefined,
): string | undefined {
  if (value === undefined || value === null) {
    return undefined
  }

  // Only allow digits
  const sanitized = value.replace(/[^0-9-]/g, '')
  return sanitized.length > 0 ? sanitized : undefined
}

/**
 * Specifically sanitizes a hotel code which might contain alphanumeric characters
 * and could be subject to path traversal attacks
 * @param value The hotel code to sanitize
 * @returns The sanitized hotel code or undefined if invalid
 */
export function sanitizeHotelCode(
  value: string | null | undefined,
): string | undefined {
  if (value === undefined || value === null) {
    return undefined
  }

  try {
    // First try to decode any URL encoding
    let decoded = decodeURIComponent(value)

    // Remove leading dots, slashes and their URL-encoded versions
    // This handles cases like ".%2f24646773533ad4b699db0940665c99a0" or "...%2f24646773533ad4b699db0940665c99a0"
    decoded = decoded.replace(/^[.\/%]*(?:\.\.|%2e%2e|\.\/|\.\\|\/|\\)*/gi, '')

    // Extract only alphanumeric characters (and some special chars if needed)
    // Adjust the regex pattern based on your valid hotel code format
    const matched = decoded.match(/([a-zA-Z0-9_-]+)/)
    return matched ? matched[0] : undefined
  } catch (error) {
    // If decoding fails, apply direct extraction
    // Remove any path traversal prefixes
    const cleaned = value.replace(
      /^[.\/%]*(?:\.\.|%2e%2e|\.\/|\.\\|\/|\\)*/gi,
      '',
    )

    // Extract only valid characters
    const matched = cleaned.match(/([a-zA-Z0-9_-]+)/)
    return matched ? matched[0] : undefined
  }
}

/**
 * Sanitizes a date string, ensuring it's in YYYY-MM-DD format
 * @param value The date string to sanitize
 * @returns The sanitized date string or undefined if invalid
 */
export function sanitizeDate(
  value: string | null | undefined,
): string | undefined {
  if (value === undefined || value === null) {
    return undefined
  }

  // Check if it matches YYYY-MM-DD format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/
  if (dateRegex.test(value)) {
    return value
  }

  return undefined
}

/**
 * Sanitizes an object of search parameters
 * @param params The search parameters object to sanitize
 * @returns The sanitized parameters object
 */
export function sanitizeSearchParams<
  T extends Record<string, string | undefined>,
>(params: T): T {
  // Create a copy of the params object to avoid mutating the original
  const sanitizedParams = { ...params } as Record<string, string | undefined>

  // Use type-safe property access with 'in' operator and type assertions
  // Sanitize common search parameters based on their expected type
  if ('areaId' in sanitizedParams) {
    sanitizedParams['areaId'] = sanitizeString(sanitizedParams['areaId'])
  }

  if ('hotelCode' in sanitizedParams) {
    sanitizedParams['hotelCode'] = sanitizeHotelCode(
      sanitizedParams['hotelCode'],
    )
  }

  if ('type' in sanitizedParams) {
    sanitizedParams['type'] = sanitizeString(sanitizedParams['type'])
  }

  if ('checkIn' in sanitizedParams) {
    sanitizedParams['checkIn'] = sanitizeDate(sanitizedParams['checkIn'])
  }

  if ('checkOut' in sanitizedParams) {
    sanitizedParams['checkOut'] = sanitizeDate(sanitizedParams['checkOut'])
  }

  if ('orderBy' in sanitizedParams) {
    sanitizedParams['orderBy'] = sanitizeString(sanitizedParams['orderBy'])
  }

  if ('searchTimestamp' in sanitizedParams) {
    sanitizedParams['searchTimestamp'] = sanitizeNumeric(
      sanitizedParams['searchTimestamp'],
    )
  }

  // Return the sanitized params, cast back to the original type
  return sanitizedParams as T
}

export default sanitizeSearchParams
